import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom'
import { Paper, Typography, Box, Button, Alert, LinearProgress, CircularProgress } from '@mui/material';
import ModalEquipmentsRegistrationLimit from './Modals/ModalEquipmentsRegistrationLimit';
import { DivFormTitle } from './styles';
import { CheckOutlined, ErrorOutlineOutlined, WarningOutlined } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import * as actions from '../../../store/actions';

const styles = {
  paper: {
    padding: '20px'
  },
  table: {
    position: 'relative',
  },
  tableRow: {
    cursor: 'pointer',
  },
  spinner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    top: '-5px',
    opacity: '1',
    zIndex: 2,
  },
  noResults: {
    height: '100px',
  },
  deleteTextConfirm: {
    marginLeft: '-30px',
    fontWeight: 'bold',
  },
  editWidth: {
    width: '90%'
  }
}

const TenantEquipmentsRegistrationLimit = (props: any) => {
  const dispatch = useDispatch();

  const tenantId = props.match.params.id;

  const tenant = useSelector((state: any) => state.tenants.tenant);
  const loadingTenant = useSelector((state: any) => state.tenants.loading);
  const registeredEquipments = useSelector((state: any) => state.tenants.registeredEquipments);

  const [open, setOpen] = useState(false);

  const fetchEquipmentsCount = useCallback(() => dispatch(actions.fetchTenantEquipmentsCount({ isSuperadmin: true, tenantId: tenantId })), [dispatch]);

  useEffect(() => {
    fetchEquipmentsCount();
  }, [])

  const equipmentsRegistrationLimit = tenant?.equipmentsRegistrationLimit || {};

  const {
    maxEquipmentsTolerated = 0,
    maxEquipments = 0,
    tolerance = 0
  } = equipmentsRegistrationLimit;

  const { reachedPercentage, reachedPercentageOverLimit } = useMemo(() => {
    let percentage = (maxEquipments > 0) ? parseFloat((registeredEquipments/maxEquipments * 100).toFixed(2)) : 100;
    percentage = percentage > 100 ? 100 : percentage;

    let percentageOverLimit = (registeredEquipments > maxEquipments) ? parseFloat(((registeredEquipments - maxEquipments)/(maxEquipmentsTolerated - maxEquipments) * 100).toFixed(2)) : 0;
    percentageOverLimit = percentageOverLimit > 100 ? 100 : percentageOverLimit;

    return ({ reachedPercentage: percentage, reachedPercentageOverLimit: percentageOverLimit })
  }, [tenant, registeredEquipments]);

  
  const BarWithinLimit = (
    <Box sx={{background: '#eee', position: 'relative', borderRadius: '2px 0px 0px 2px', width: `${100 - tolerance}%`, height: '10px'}}>
      <Box
        sx={{
          background: 'green',
          position: 'absolute',
          left: 0,
          borderRadius: '2px 0px 0px 2px',
          width: `${reachedPercentage}%`,
          height: '100%'
        }}
      />
    </Box>
  );

  const BarOverLimit = (
    <Box sx={{background: '#eee', position: 'relative', borderRadius: '0px 2px 2px 0px', width: `${tolerance}%`, height: '10px', marginLeft: '2px'}}>
      <Box
        sx={{
          background: '#d32f2f',
          position: 'absolute',
          left: 0,
          borderRadius: '0px 2px 2px 0px',
          width: `${reachedPercentageOverLimit}%`,
          height: '100%'
        }}
      />
    </Box>
  )

  const alertIcon = useMemo(() => {
    if (reachedPercentage > 70 && reachedPercentage < 90) {
      return <WarningOutlined />
    } else if (reachedPercentage > 90) {
      return <ErrorOutlineOutlined />
    } else {
      return <CheckOutlined />
    }
  }, [tenant, reachedPercentage, registeredEquipments])

  const alertType = useMemo(() => {
    if (reachedPercentage > 70 && reachedPercentage < 90) {
      return 'warning'
    } else if (reachedPercentage > 90) {
      return 'error'
    } else {
      return 'success'
    }
  }, [tenant, reachedPercentage, registeredEquipments])

  return <>
    <ModalEquipmentsRegistrationLimit
      open={open}
      onClose={() => setOpen(false)}
    />
    <Paper style={styles.paper}>
        <DivFormTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant='h6'>Limite de Equipamentos</Typography>
          <Button
            variant="outlined"
            disabled={loadingTenant}
            onClick={() => setOpen(true)}
          >
            Alterar plano
          </Button>
        </DivFormTitle>

        <Box>
          {loadingTenant && <CircularProgress />}

          {(!loadingTenant && !isEmpty(equipmentsRegistrationLimit) && (equipmentsRegistrationLimit.maxEquipments > 0 || equipmentsRegistrationLimit.tolerance > 0)) && (
            <>
              <Alert
                variant="standard"
                color={alertType}
                icon={alertIcon}
                sx={{margin: '20px 0'}}
              >
                {`O tenant atingiu ${reachedPercentage}% do limite de equipamentos permitidos pelo plano.`}
              </Alert>

              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', marginBottom: '20px'}}>
                <Typography>{`${registeredEquipments}/${maxEquipments} Equipamentos cadastrados`}</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
                  {BarWithinLimit}
                  {BarOverLimit}
                </Box>
              </Box>

              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center'}}>
                <Typography>Limite de excedente de equipamento</Typography>
                <Typography sx={{fontSize: 10, marginBottom: 1}}>Após aplicar a porcentagem de tolerância, caso o adicional de equipamntos for um número fracionado, o mesmo será arredondado para o número inteiro maior e mais próximo do valor</Typography>
                <Typography>{`${tolerance}%`}</Typography>
              </Box>
            </>
          )}

          {(!loadingTenant && (isEmpty(equipmentsRegistrationLimit) || (equipmentsRegistrationLimit.maxEquipments <= 0 && equipmentsRegistrationLimit.tolerance <= 0))) && (
            <Alert
              variant="outlined"
              color="warning"
              icon={<WarningOutlined />}
              sx={{margin: '20px 0', padding: '6px 16px'}}
            >
              Configure um plano para gerenciar o registro de equipamentos
            </Alert>
          )}
      </Box>
    </Paper>
  </>;
}

export default withRouter(TenantEquipmentsRegistrationLimit);

